import { useState } from "react";
import { Pin } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { Note } from "@/lib/types";
import { shortDate } from "@/lib/format";
import { Button, Card, Loading, Empty, toast } from "@/components/ui";
import { renderMarkdown } from "@/lib/markdown";
import { useMe } from "@/app/MeContext";

export type NoteScope = { accountId: string; partnerId?: undefined } | { partnerId: string; accountId?: undefined };

/** Notes for an account or a partner; the API scopes by whichever id is given. */
export function NotesTab({ scope }: { scope: NoteScope }) {
  const me = useMe();
  const id = scope.accountId ?? scope.partnerId;
  const owner = scope.accountId ? `account:${scope.accountId}` : `partner:${scope.partnerId}`;
  const { data, loading, refetch } = useQuery<Note[]>(`notes:${id}`, () => api.get<Note[]>(scope.accountId ? `/notes?accountId=${scope.accountId}` : `/notes?partnerId=${scope.partnerId}`), [id]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { await api.post("/notes", { accountId: scope.accountId ?? null, partnerId: scope.partnerId ?? null, body }); setBody(""); refetch(); invalidate(owner); } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  return (
    <div className="two-col">
      <div className="left">
        <Card className="pad col gap-8">
          <textarea className="textarea" placeholder="Write a note. Markdown works: **bold**, - lists, [links](https://...)" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") add(); }} />
          <div className="row"><span className="xs dim">⌘↵ to save</span><Button variant="primary" className="ml-auto" disabled={busy || !body.trim()} onClick={add}>Add note</Button></div>
        </Card>
        {loading && !data ? <Loading /> : (data ?? []).length === 0 ? <Card><Empty>No notes yet.</Empty></Card> : (data ?? []).map((n) => (
          <Card key={n.id} className="pad col gap-8">
            <div className="row gap-8">
              {n.pinned && <Pin size={13} className="muted" />}
              <span className="small muted">{n.authorName} · {shortDate(n.createdAt)}{n.updatedAt !== n.createdAt ? " · edited" : ""}</span>
              <span className="ml-auto row gap-10">
                <button className="btn-link small muted" onClick={async () => { await api.patch(`/notes/${n.id}`, { pinned: !n.pinned }); refetch(); invalidate(owner); }}>{n.pinned ? "Unpin" : "Pin"}</button>
                {(n.authorId === me.id || me.role === "admin") && <>
                  <button className="btn-link small muted" onClick={() => { setEditing(n.id); setEditBody(n.body); }}>Edit</button>
                  <button className="btn-link small muted" onClick={async () => { if (confirm("Delete this note?")) { await api.del(`/notes/${n.id}`); refetch(); invalidate(owner); } }}>Delete</button>
                </>}
              </span>
            </div>
            {editing === n.id ? (
              <>
                <textarea className="textarea" value={editBody} onChange={(e) => setEditBody(e.target.value)} />
                <div className="row gap-8"><Button size="md" onClick={() => setEditing(null)}>Cancel</Button><Button size="md" variant="primary" onClick={async () => { await api.patch(`/notes/${n.id}`, { body: editBody }); setEditing(null); refetch(); invalidate(owner); }}>Save</Button></div>
              </>
            ) : <div className="prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(n.body) }} />}
          </Card>
        ))}
      </div>
    </div>
  );
}
